import Link from "next/link";
import type { ComponentProps } from "react";
import * as gtag from "@lib/utilities/analytics";

/**
 * The <a> renderer for CMS rich text.
 *
 * ⭐ PORTFOLIO-CANONICAL COPY (owner, 2026-09-07): this file merges
 * av-sweetandbitter-web's CustomMDXLink with the fixes made here (external
 * links hardened with rel, mailto:/tel:/#fragment left as plain anchors,
 * both consent-href conventions). When improving the rich-text link
 * behaviour, change THIS file first and back-port; sweetandbitter has
 * received the fixes but still opens consent through its redux store.
 *
 * Consent opens via the `pw-open-consent` window event (no redux here).
 *
 * Behaviours markdown alone cannot express:
 *  - "action:showCookieBanner" (the S&B convention, so copy moved between
 *    the two projects keeps working) and "#cookie-settings" (this site's
 *    footer-legal menu convention) both render the consent-settings BUTTON —
 *    editors can offer «αλλάξτε τις προτιμήσεις σας» inside the
 *    cookie-policy body, where GDPR text wants it
 *  - internal "/..." hrefs → next/link (client-side navigation, no reload)
 *  - external hrefs → new tab with rel noopener/noreferrer
 */
const CONSENT_HREFS = new Set(["action:showCookieBanner", "#cookie-settings"]);

const CustomMDXLink = (props: ComponentProps<"a">) => {
  const { href, children, ...rest } = props;

  if (href && CONSENT_HREFS.has(href)) {
    const open = () => window.dispatchEvent(new CustomEvent("pw-open-consent"));
    return (
      // an anchor sans href so it inherits the surrounding container's link
      // styling instead of carrying its own (S&B's reasoning, kept)
      <a role="button" tabIndex={0} className="cursor-pointer" onClick={open}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && open()} {...rest}>
        {children}
      </a>
    );
  }

  if (href?.startsWith("/")) {
    return <Link href={href} {...rest}>{children}</Link>;
  }

  if (href?.startsWith("http")) {
    return <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>;
  }

  // a tel: inside CMS rich text is the same conversion affordance as every
  // PhoneLink — it joins the phone_click taxonomy under its own placement
  if (href?.startsWith("tel:")) {
    return (
      <a href={href} {...rest}
        onClick={() => gtag.event("phone_click", { event_category: "conversion", event_label: "rich-text" })}>
        {children}
      </a>
    );
  }

  // a mailto: is the written-enquiry twin of tel: — same taxonomy
  if (href?.startsWith("mailto:")) {
    return (
      <a href={href} {...rest}
        onClick={() => gtag.event("email_click", { event_category: "conversion", event_label: "rich-text" })}>
        {children}
      </a>
    );
  }

  // #fragments — the plain anchor is exactly right
  return <a href={href} {...rest}>{children}</a>;
};

export default CustomMDXLink;
